import React, { useEffect, useRef, useState } from 'react';
import { faRightFromBracket, faUser, faAddressCard, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactDOM from "react-dom";

const AccountDropdown = ({ jumpToHome, logout, onChangeChildren }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [userInfo, setUserInfo] = useState(null);
	const dropdownRef = useRef(null);

	useEffect(() => {
		const storedUserInfo = localStorage.getItem('user_info');
		if (storedUserInfo && storedUserInfo !== 'undefined') {
			try {
				setUserInfo(JSON.parse(storedUserInfo));
			} catch (e) {}
		}
	}, []);

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			const portalDropdown = document.querySelector('[data-dropdown-portal]');
			if (portalDropdown && portalDropdown.contains(event.target)) {
				return;
			}

			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleProfileClick = () => {
		setIsOpen(false);
		jumpToHome();
		setTimeout(() => {
			onChangeChildren('profile');
		}, 100);
	};

	const handleResetPasswordClick = () => {
		setIsOpen(false);
		jumpToHome();
		setTimeout(() => {
			onChangeChildren('reset-password');
		}, 100);
	};

	return (
		<div className="relative z-20" ref={dropdownRef}>
			<div className="w-full flex items-center justify-end px-4">
				<button
					className="flex items-center gap-2 hover:bg-gray-100 rounded-md px-3 py-1"
					onClick={toggleDropdown}
				>
					<FontAwesomeIcon icon={faUser} className="text-gray-800" />
					<span className="text-gray-800 font-medium">
						{userInfo?.username || 'Account'}
					</span>
					<svg
						className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
						viewBox="0 0 20 20"
						fill="currentColor"
					>
						<path
							fillRule="evenodd"
							d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
							clipRule="evenodd"
						/>
					</svg>
				</button>
			</div>

			{isOpen && ReactDOM.createPortal(
				<div
					data-dropdown-portal
					className="fixed w-60 rounded-lg border-gray-300 shadow-md z-[9999] bg-gray-200"
					style={{
						top: `${dropdownRef.current?.getBoundingClientRect().bottom + 8}px`,
						right: '16px'
					}}
				>
					{userInfo && (
						<div className="px-4 py-3 border-b border-gray-300">
							<p className="text-sm font-medium text-gray-800">{userInfo.username}</p>
							<p className="text-xs text-gray-600">{userInfo.email}</p>
						</div>
					)}
					<div
						className="flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-blue-300 cursor-pointer"
						onClick={(e) => {
							handleProfileClick();
						}}
					>
						<FontAwesomeIcon icon={faAddressCard} className="text-gray-800" />
						<span>Profile</span>
					</div>
					<div
						className="flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-blue-300 cursor-pointer"
						onClick={(e) => {
							handleResetPasswordClick();
						}}
					>
						<FontAwesomeIcon icon={faLock} className="text-gray-800" />
						<span>Reset Password</span>
					</div>
					<div
						className="flex items-center gap-2 px-4 py-3 text-gray-800 rounded-b-lg hover:bg-blue-300 cursor-pointer"
						onClick={(e) => {
							logout();
							setIsOpen(false);
						}}
					>
						<FontAwesomeIcon icon={faRightFromBracket} className="text-gray-800" />
						<span>Log out</span>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export function Tabs({ children, logout, onChange, onChangeChildren }) {
	return (
		<div className="flex border-b border-gray-200 z-10 rounded-lg justify-between bg-gray-100">
			<div className="flex">
				{React.Children.map(children, (child) =>
					React.cloneElement(child)
				)}
			</div>
			<AccountDropdown
				jumpToHome={() => onChange('my-profile')}
				logout={logout}
				onChangeChildren={onChangeChildren}
			/>
		</div>
	);
}

export function Tab({ id, label, onChange, onChangeChildren, icon, children }) {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const buttonRef = useRef(null);
	const popoverRef = useRef(null);
	const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				buttonRef.current &&
				!buttonRef.current.contains(event.target) &&
				popoverRef.current &&
				!popoverRef.current.contains(event.target)
			) {
				setIsPopoverOpen(false);
			}
		};

		if (isPopoverOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isPopoverOpen]);

	const togglePopover = () => {
		if (id === 'my-profile') {
			onChange(id);
			return;
		}

		if (!children) {
			onChange(id);
			return;
		}

		if (!isPopoverOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setPopoverPosition({
				top: rect.bottom + 8,
				left: rect.left
			});
		}

		setIsPopoverOpen(!isPopoverOpen);
	};

	return (
		<div className="relative">
			<button
				ref={buttonRef}
				key={id}
				className="py-2 px-4 text-sm border-gray-300 rounded-t-lg text-gray-600 hover:bg-gray-200"
				onClick={togglePopover}
			>
				<FontAwesomeIcon icon={icon} className="mr-2 text-gray-500" />
				{label}
			</button>

			{children && isPopoverOpen && id !== 'my-profile' && (
				ReactDOM.createPortal(
					<div
						ref={popoverRef}
						className="bg-white border border-gray-300 rounded-lg shadow-md z-[999]"
						style={{
							position: 'fixed',
							top: `${popoverPosition.top}px`,
							left: `${popoverPosition.left}px`,
							width: '12rem'
						}}
					>
						{children.map((child) => (
							<button
								key={child.id}
								className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-200 cursor-pointer"
								onClick={() => {
									onChangeChildren(child.id);
									onChange(id);
									setIsPopoverOpen(false);
								}}
							>
								<FontAwesomeIcon icon={child.icon} className="mr-2 text-gray-600" />
								{child.label}
							</button>
						))}
					</div>,
					document.body
				)
			)}
		</div>
	);
}
